/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Modal,
  TextInput,
  Alert,
  ActivityIndicator,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome,
  AntDesign,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Image } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  projects: `${API_BASE_URL}/api/projects`,
  companyProfile: `${API_BASE_URL}/api/company/profile`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id')
                      || await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId), company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Custom date picker component using only standard React Native components
const CustomDatePicker = ({
  isVisible,
  onClose,
  onSelectDate,
  initialDate,
}) => {
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date());
  const [selectedYear, setSelectedYear] = useState(
    initialDate ? initialDate.getFullYear() : new Date().getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = useState(
    initialDate ? initialDate.getMonth() : new Date().getMonth()
  );
  const [selectedDay, setSelectedDay] = useState(
    initialDate ? initialDate.getDate() : new Date().getDate()
  );

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Generate years (current year - 5 to current year + 10)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 16 }, (_, i) => currentYear - 5 + i);

  // Generate days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Update selected date when year, month, or day changes
  useEffect(() => {
    const newDate = new Date(selectedYear, selectedMonth, selectedDay);
    setSelectedDate(newDate);
  }, [selectedYear, selectedMonth, selectedDay]);

  // Handle confirm selection
  const handleConfirm = () => {
    onSelectDate(selectedDate);
    onClose();
  };

  if (!isVisible) return null;

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={isVisible}
      onRequestClose={onClose}
    >
      <View style={customDatePickerStyles.modalOverlay}>
        <View style={customDatePickerStyles.modalContent}>
          <View style={customDatePickerStyles.header}>
            <Text style={customDatePickerStyles.title}>Select Date</Text>
            <TouchableOpacity
              onPress={onClose}
              style={customDatePickerStyles.closeButton}
            >
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>

          <View style={customDatePickerStyles.dateDisplay}>
            <Text style={customDatePickerStyles.dateText}>
              {selectedDate.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </Text>
          </View>

          <View style={customDatePickerStyles.pickerContainer}>
            {/* Month Picker */}
            <View style={customDatePickerStyles.pickerColumn}>
              <Text style={customDatePickerStyles.pickerLabel}>Month</Text>
              <ScrollView
                style={customDatePickerStyles.picker}
                showsVerticalScrollIndicator={false}
              >
                {months.map((month, index) => (
                  <TouchableOpacity
                    key={month}
                    style={[
                      customDatePickerStyles.pickerItem,
                      selectedMonth === index &&
                        customDatePickerStyles.pickerItemSelected,
                    ]}
                    onPress={() => {
                      setSelectedMonth(index);
                      // Adjust day if the new month has fewer days
                      const maxDays = getDaysInMonth(selectedYear, index);
                      if (selectedDay > maxDays) {
                        setSelectedDay(maxDays);
                      }
                    }}
                  >
                    <Text
                      style={[
                        customDatePickerStyles.pickerItemText,
                        selectedMonth === index &&
                          customDatePickerStyles.pickerItemTextSelected,
                      ]}
                    >
                      {month}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            {/* Day Picker */}
            <View style={customDatePickerStyles.pickerColumn}>
              <Text style={customDatePickerStyles.pickerLabel}>Day</Text>
              <ScrollView
                style={customDatePickerStyles.picker}
                showsVerticalScrollIndicator={false}
              >
                {days.map(day => (
                  <TouchableOpacity
                    key={day}
                    style={[
                      customDatePickerStyles.pickerItem,
                      selectedDay === day &&
                        customDatePickerStyles.pickerItemSelected,
                    ]}
                    onPress={() => setSelectedDay(day)}
                  >
                    <Text
                      style={[
                        customDatePickerStyles.pickerItemText,
                        selectedDay === day &&
                          customDatePickerStyles.pickerItemTextSelected,
                      ]}
                    >
                      {day}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            {/* Year Picker */}
            <View style={customDatePickerStyles.pickerColumn}>
              <Text style={customDatePickerStyles.pickerLabel}>Year</Text>
              <ScrollView
                style={customDatePickerStyles.picker}
                showsVerticalScrollIndicator={false}
              >
                {years.map(year => (
                  <TouchableOpacity
                    key={year}
                    style={[
                      customDatePickerStyles.pickerItem,
                      selectedYear === year &&
                        customDatePickerStyles.pickerItemSelected,
                    ]}
                    onPress={() => {
                      setSelectedYear(year);
                      // Adjust day if the new year/month combination has fewer days (e.g., Feb 29 in non-leap years)
                      const maxDays = getDaysInMonth(year, selectedMonth);
                      if (selectedDay > maxDays) {
                        setSelectedDay(maxDays);
                      }
                    }}
                  >
                    <Text
                      style={[
                        customDatePickerStyles.pickerItemText,
                        selectedYear === year &&
                          customDatePickerStyles.pickerItemTextSelected,
                      ]}
                    >
                      {year}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>

          <TouchableOpacity
            style={customDatePickerStyles.confirmButton}
            onPress={handleConfirm}
          >
            <Text style={customDatePickerStyles.confirmButtonText}>
              Confirm
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const customDatePickerStyles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    padding: 20,
    maxHeight: '80%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
  },
  closeButton: {
    padding: 4,
  },
  dateDisplay: {
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 10,
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
  },
  dateText: {
    fontSize: 18,
    fontWeight: '500',
    color: '#0e4e96',
  },
  pickerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  pickerColumn: {
    flex: 1,
    marginHorizontal: 4,
  },
  pickerLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginBottom: 8,
    textAlign: 'center',
  },
  picker: {
    height: 150,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
  },
  pickerItem: {
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pickerItemSelected: {
    backgroundColor: '#e0f2fe',
  },
  pickerItemText: {
    fontSize: 16,
    color: '#334155',
  },
  pickerItemTextSelected: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  confirmButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default function ProjectMilestonesScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { project } = route.params;

  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentDateField, setCurrentDateField] = useState(null);
  const [showAddMilestoneModal, setShowAddMilestoneModal] = useState(false);
  const [showEditMilestoneModal, setShowEditMilestoneModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [milestone, setMilestone] = useState({
    title: '',
    dueDate: new Date(),
    status: 'Not Started',
  });
  const [projectData, setProjectData] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [companyLogo, setCompanyLogo] = useState(null);

  // Milestone status options
  const milestoneStatusOptions = [
    'Not Started',
    'In Progress',
    'Completed',
    'On Hold',
  ];

  // Filter options
  const filterOptions = ['All', ...milestoneStatusOptions];

  // Load company logo on mount
  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Load project data on component mount
  useEffect(() => {
    if (project) {
      setProjectData(project);
    }
  }, [project]);

  // Handle back button navigation
  const handleGoBack = () => {
    navigation.goBack();
  };

  // Format date
  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get milestone status color
  const getMilestoneStatusColor = status => {
    switch (status) {
      case 'Completed':
        return '#10B981'; // Green
      case 'In Progress':
        return '#F59E0B'; // Orange
      case 'On Hold':
        return '#8B5CF6'; // Purple
      case 'Not Started':
        return '#64748B'; // Gray
      default:
        return '#64748B'; // Gray
    }
  };

  // Handle date selection
  const handleDateSelection = selectedDate => {
    setMilestone({
      ...milestone,
      dueDate: selectedDate,
    });
    setShowDatePicker(false);
  };

  // Handle add milestone
  const handleAddMilestone = () => {
    if (milestone.title.trim() === '') {
      Alert.alert('Error', 'Please enter a title for the milestone');
      return;
    }

    const newMilestone = {
      ...milestone,
      id: `m${Date.now()}`,
    };

    const updatedProject = {
      ...projectData,
      milestones: [...projectData.milestones, newMilestone],
    };

    setProjectData(updatedProject);
    setMilestone({ title: '', dueDate: new Date(), status: 'Not Started' });
    setShowAddMilestoneModal(false);

    // Show success message
    Alert.alert('Success', 'Milestone added successfully');
  };

  // Handle edit milestone
  const handleEditMilestone = () => {
    if (milestone.title.trim() === '') {
      Alert.alert('Error', 'Please enter a title for the milestone');
      return;
    }

    const updatedMilestones = projectData.milestones.map(item =>
      item.id === selectedMilestone.id ? { ...milestone, id: item.id } : item
    );

    const updatedProject = {
      ...projectData,
      milestones: updatedMilestones,
    };

    setProjectData(updatedProject);
    setMilestone({ title: '', dueDate: new Date(), status: 'Not Started' });
    setSelectedMilestone(null);
    setShowEditMilestoneModal(false);

    // Show success message
    Alert.alert('Success', 'Milestone updated successfully');
  };

  // Handle delete milestone
  const handleDeleteMilestone = () => {
    const updatedMilestones = projectData.milestones.filter(
      item => item.id !== selectedMilestone.id
    );

    const updatedProject = {
      ...projectData,
      milestones: updatedMilestones,
    };

    setProjectData(updatedProject);
    setSelectedMilestone(null);
    setShowConfirmDeleteModal(false);

    // Show success message
    Alert.alert('Success', 'Milestone deleted successfully');
  };

  // Open edit milestone modal
  const openEditMilestoneModal = milestone => {
    setSelectedMilestone(milestone);
    setMilestone({
      title: milestone.title,
      dueDate: new Date(milestone.dueDate),
      status: milestone.status,
    });
    setShowEditMilestoneModal(true);
  };

  // Open delete milestone confirmation
  const openDeleteMilestoneConfirmation = milestone => {
    setSelectedMilestone(milestone);
    setShowConfirmDeleteModal(true);
  };

  // Filter milestones by status
  const getFilteredMilestones = () => {
    if (!projectData || !projectData.milestones) return [];

    if (filterStatus === 'All') {
      return projectData.milestones;
    } else {
      return projectData.milestones.filter(
        milestone => milestone.status === filterStatus
      );
    }
  };

  // Sort milestones by due date
  const getSortedMilestones = () => {
    const filtered = getFilteredMilestones();

    return filtered.sort((a, b) => {
      const dateA = new Date(a.dueDate);
      const dateB = new Date(b.dueDate);
      return dateA - dateB;
    });
  };

  // Calculate project timeline progress
  const calculateTimelineProgress = () => {
    if (!projectData) return 0;

    const startDate = new Date(projectData.startDate);
    const endDate = new Date(projectData.endDate);
    const today = new Date();

    const totalDuration = endDate - startDate;
    const elapsedDuration = today - startDate;

    if (elapsedDuration <= 0) return 0;
    if (elapsedDuration >= totalDuration) return 100;

    return Math.round((elapsedDuration / totalDuration) * 100);
  };

  // Calculate milestone completion percentage
  const calculateMilestoneCompletion = () => {
    if (
      !projectData ||
      !projectData.milestones ||
      projectData.milestones.length === 0
    )
      return 0;

    const completedMilestones = projectData.milestones.filter(
      milestone => milestone.status === 'Completed'
    );
    return Math.round(
      (completedMilestones.length / projectData.milestones.length) * 100
    );
  };

  if (!projectData) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading milestones...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header with Back Button, Company Logo, and Title */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
          ) : (
            <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
          )}
          <Text style={styles.headerTitle}>Project Milestones</Text>
        </View>

        <View style={{ width: 30 }} />
      </View>

      {/* Project Title */}
      <View style={styles.projectHeader}>
        <Text style={styles.projectTitle}>{projectData.title}</Text>
      </View>

      {/* Progress Summary */}
      <View style={styles.progressSummary}>
        <View style={styles.progressItem}>
          <Text style={styles.progressLabel}>Timeline Progress</Text>
          <View style={styles.progressBarContainer}>
            <View style={styles.progressBar}>
              <View
                style={[
                  styles.progressFill,
                  {
                    width: `${calculateTimelineProgress()}%`,
                    backgroundColor: '#0e4e96',
                  },
                ]}
              />
            </View>
            <Text style={styles.progressText}>
              {calculateTimelineProgress()}%
            </Text>
          </View>
        </View>

        <View style={styles.progressItem}>
          <Text style={styles.progressLabel}>Milestones Completed</Text>
          <View style={styles.progressBarContainer}>
            <View style={styles.progressBar}>
              <View
                style={[
                  styles.progressFill,
                  {
                    width: `${calculateMilestoneCompletion()}%`,
                    backgroundColor: '#10B981',
                  },
                ]}
              />
            </View>
            <Text style={styles.progressText}>
              {calculateMilestoneCompletion()}%
            </Text>
          </View>
        </View>
      </View>

      {/* Filter and Add Button */}
      <View style={styles.actionsContainer}>
        <View style={styles.filterContainer}>
          <Text style={styles.filterLabel}>Filter:</Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.filterOptions}
          >
            {filterOptions.map(status => (
              <TouchableOpacity
                key={status}
                style={[
                  styles.filterOption,
                  filterStatus === status && styles.filterOptionActive,
                  filterStatus === status && {
                    borderColor:
                      status === 'All'
                        ? '#0e4e96'
                        : getMilestoneStatusColor(status),
                  },
                ]}
                onPress={() => setFilterStatus(status)}
              >
                {status !== 'All' && (
                  <MaterialCommunityIcons
                    name={
                      status === 'Completed'
                        ? 'check-circle'
                        : status === 'In Progress'
                          ? 'progress-clock'
                          : status === 'On Hold'
                            ? 'pause-circle'
                            : 'circle-outline'
                    }
                    size={16}
                    color={
                      status === 'All'
                        ? '#0e4e96'
                        : getMilestoneStatusColor(status)
                    }
                  />
                )}
                <Text
                  style={[
                    styles.filterOptionText,
                    filterStatus === status && styles.filterOptionTextActive,
                    filterStatus === status && {
                      color:
                        status === 'All'
                          ? '#0e4e96'
                          : getMilestoneStatusColor(status),
                    },
                  ]}
                >
                  {status}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        <TouchableOpacity
          style={styles.addButton}
          onPress={() => setShowAddMilestoneModal(true)}
        >
          <Ionicons name="add" size={20} color="#fff" />
          <Text style={styles.addButtonText}>Add Milestone</Text>
        </TouchableOpacity>
      </View>

      {/* Milestones List */}
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.contentContainer}
      >
        {getSortedMilestones().length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons
              name="flag-checkered"
              size={40}
              color="#CBD5E1"
            />
            <Text style={styles.emptyStateText}>
              {filterStatus === 'All'
                ? 'No milestones added yet'
                : `No ${filterStatus} milestones found`}
            </Text>
          </View>
        ) : (
          getSortedMilestones().map(milestone => (
            <View key={milestone.id} style={styles.milestoneCard}>
              <View style={styles.milestoneHeader}>
                <Text style={styles.milestoneTitle}>{milestone.title}</Text>
                <View style={styles.milestoneActions}>
                  <TouchableOpacity
                    style={styles.milestoneAction}
                    onPress={() => openEditMilestoneModal(milestone)}
                  >
                    <Ionicons name="create-outline" size={20} color="#64748B" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.milestoneAction}
                    onPress={() => openDeleteMilestoneConfirmation(milestone)}
                  >
                    <Ionicons name="trash-outline" size={20} color="#64748B" />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.milestoneDetails}>
                <View style={styles.milestoneDetail}>
                  <Ionicons name="calendar-outline" size={16} color="#64748B" />
                  <Text style={styles.milestoneDetailText}>
                    Due: {formatDate(milestone.dueDate)}
                  </Text>
                </View>

                <View
                  style={[
                    styles.milestoneStatusBadge,
                    {
                      backgroundColor:
                        getMilestoneStatusColor(milestone.status) + '20',
                    },
                  ]}
                >
                  <MaterialCommunityIcons
                    name={
                      milestone.status === 'Completed'
                        ? 'check-circle'
                        : milestone.status === 'In Progress'
                          ? 'progress-clock'
                          : milestone.status === 'On Hold'
                            ? 'pause-circle'
                            : 'circle-outline'
                    }
                    size={16}
                    color={getMilestoneStatusColor(milestone.status)}
                  />
                  <Text
                    style={[
                      styles.milestoneStatusText,
                      { color: getMilestoneStatusColor(milestone.status) },
                    ]}
                  >
                    {milestone.status}
                  </Text>
                </View>
              </View>

              {/* Timeline indicator */}
              <View style={styles.timelineIndicator}>
                <View
                  style={[
                    styles.timelineIndicatorDot,
                    {
                      backgroundColor: getMilestoneStatusColor(
                        milestone.status
                      ),
                    },
                  ]}
                />
                <View style={styles.timelineIndicatorLine} />
              </View>
            </View>
          ))
        )}
      </ScrollView>

      {/* Add Milestone Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showAddMilestoneModal}
        onRequestClose={() => setShowAddMilestoneModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Milestone</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowAddMilestoneModal(false)}
              >
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Title *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter milestone title"
                placeholderTextColor="#94A3B8"
                value={milestone.title}
                onChangeText={text =>
                  setMilestone({ ...milestone, title: text })
                }
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Due Date *</Text>
              <TouchableOpacity
                style={styles.dateInput}
                onPress={() => setShowDatePicker(true)}
              >
                <Text style={styles.dateText}>
                  {formatDate(milestone.dueDate)}
                </Text>
                <Ionicons name="calendar-outline" size={20} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Status</Text>
              <View style={styles.optionsContainer}>
                {milestoneStatusOptions.map(status => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.optionButton,
                      milestone.status === status && styles.optionButtonActive,
                      milestone.status === status && {
                        borderColor: getMilestoneStatusColor(status),
                      },
                    ]}
                    onPress={() =>
                      setMilestone({ ...milestone, status: status })
                    }
                  >
                    <MaterialCommunityIcons
                      name={
                        status === 'Completed'
                          ? 'check-circle'
                          : status === 'In Progress'
                            ? 'progress-clock'
                            : status === 'On Hold'
                              ? 'pause-circle'
                              : 'circle-outline'
                      }
                      size={16}
                      color={getMilestoneStatusColor(status)}
                    />
                    <Text
                      style={[
                        styles.optionButtonText,
                        milestone.status === status &&
                          styles.optionButtonTextActive,
                        milestone.status === status && {
                          color: getMilestoneStatusColor(status),
                        },
                      ]}
                    >
                      {status}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <TouchableOpacity
              style={styles.modalActionButton}
              onPress={handleAddMilestone}
            >
              <Text style={styles.modalActionButtonText}>Add Milestone</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Edit Milestone Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showEditMilestoneModal}
        onRequestClose={() => setShowEditMilestoneModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Milestone</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowEditMilestoneModal(false)}
              >
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Title *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter milestone title"
                placeholderTextColor="#94A3B8"
                value={milestone.title}
                onChangeText={text =>
                  setMilestone({ ...milestone, title: text })
                }
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Due Date *</Text>
              <TouchableOpacity
                style={styles.dateInput}
                onPress={() => setShowDatePicker(true)}
              >
                <Text style={styles.dateText}>
                  {formatDate(milestone.dueDate)}
                </Text>
                <Ionicons name="calendar-outline" size={20} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Status</Text>
              <View style={styles.optionsContainer}>
                {milestoneStatusOptions.map(status => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.optionButton,
                      milestone.status === status && styles.optionButtonActive,
                      milestone.status === status && {
                        borderColor: getMilestoneStatusColor(status),
                      },
                    ]}
                    onPress={() =>
                      setMilestone({ ...milestone, status: status })
                    }
                  >
                    <MaterialCommunityIcons
                      name={
                        status === 'Completed'
                          ? 'check-circle'
                          : status === 'In Progress'
                            ? 'progress-clock'
                            : status === 'On Hold'
                              ? 'pause-circle'
                              : 'circle-outline'
                      }
                      size={16}
                      color={getMilestoneStatusColor(status)}
                    />
                    <Text
                      style={[
                        styles.optionButtonText,
                        milestone.status === status &&
                          styles.optionButtonTextActive,
                        milestone.status === status && {
                          color: getMilestoneStatusColor(status),
                        },
                      ]}
                    >
                      {status}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <TouchableOpacity
              style={styles.modalActionButton}
              onPress={handleEditMilestone}
            >
              <Text style={styles.modalActionButtonText}>Update Milestone</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Confirm Delete Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showConfirmDeleteModal}
        onRequestClose={() => setShowConfirmDeleteModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.confirmModalContent}>
            <MaterialCommunityIcons
              name="alert-circle-outline"
              size={40}
              color="#EF4444"
            />
            <Text style={styles.confirmModalTitle}>Delete Milestone?</Text>
            <Text style={styles.confirmModalText}>
              Are you sure you want to delete this milestone? This action cannot
              be undone.
            </Text>

            <View style={styles.confirmModalButtons}>
              <TouchableOpacity
                style={styles.cancelModalButton}
                onPress={() => setShowConfirmDeleteModal(false)}
              >
                <Text style={styles.cancelModalButtonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.deleteModalButton}
                onPress={handleDeleteMilestone}
              >
                <Text style={styles.deleteModalButtonText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Date Picker Modal */}
      <CustomDatePicker
        isVisible={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onSelectDate={handleDateSelection}
        initialDate={milestone.dueDate}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 5,
  },
  headerCenter: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    resizeMode: 'cover',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748B',
  },
  projectHeader: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  projectTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  progressSummary: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  progressItem: {
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  progressBarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  progressBar: {
    flex: 1,
    height: 6,
    backgroundColor: '#e2e8f0',
    borderRadius: 3,
    marginRight: 8,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
    width: 35,
    textAlign: 'right',
  },
  actionsContainer: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  filterContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginRight: 8,
  },
  filterOptions: {
    flexDirection: 'row',
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 16,
    marginRight: 8,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  filterOptionActive: {
    backgroundColor: '#fff',
    borderWidth: 1,
  },
  filterOptionText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  filterOptionTextActive: {
    fontWeight: '600',
  },
  addButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  addButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 4,
  },
  scrollContainer: {
    flex: 1,
    backgroundColor: '#f1f5f9',
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    backgroundColor: '#fff',
    borderRadius: 12,
    marginTop: 16,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 8,
  },
  milestoneCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
    position: 'relative',
  },
  milestoneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  milestoneTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    flex: 1,
    marginRight: 8,
  },
  milestoneActions: {
    flexDirection: 'row',
  },
  milestoneAction: {
    padding: 4,
    marginLeft: 8,
  },
  milestoneDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  milestoneDetail: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  milestoneDetailText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 6,
  },
  milestoneStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  milestoneStatusText: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 4,
  },
  timelineIndicator: {
    position: 'absolute',
    left: -8,
    top: 0,
    bottom: 0,
    width: 16,
    alignItems: 'center',
  },
  timelineIndicatorDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    marginTop: 16,
  },
  timelineIndicatorLine: {
    width: 2,
    flex: 1,
    backgroundColor: '#e2e8f0',
    marginTop: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    padding: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
  },
  modalCloseButton: {
    padding: 4,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#334155',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#0f172a',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  dateInput: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dateText: {
    fontSize: 16,
    color: '#0f172a',
  },
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -4,
  },
  optionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginHorizontal: 4,
    marginBottom: 8,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  optionButtonActive: {
    backgroundColor: '#fff',
    borderWidth: 1,
  },
  optionButtonText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 6,
  },
  optionButtonTextActive: {
    fontWeight: '600',
  },
  modalActionButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalActionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  confirmModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    padding: 20,
    alignItems: 'center',
  },
  confirmModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginTop: 12,
    marginBottom: 8,
  },
  confirmModalText: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 20,
  },
  confirmModalButtons: {
    flexDirection: 'row',
    width: '100%',
  },
  cancelModalButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
  },
  cancelModalButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  deleteModalButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
    backgroundColor: '#EF4444',
    borderRadius: 8,
  },
  deleteModalButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
});